import type { DemandPolicy, DemandPreview, DemandPreviewChannel, DemandSource, GrowthScope } from '@api/replenishment-planning';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { ActionIcon, Badge, Group, Loader, NumberInput, SegmentedControl, Select, Stack, TagsInput, Text, Title, Tooltip } from '@mantine/core';
import { Trash } from '@phosphor-icons/react';
import { formatCount } from '@utils/currency';
import { useMemo, type FC, type ReactElement } from 'react';

import classes from './DemandWorkbook.module.css';

type Props = {
    demand: DemandPolicy | null;
    preview: DemandPreview | null;
    previewing: boolean;
    /** The policy as edited; the parent re-reads the preview and saves on Continue. */
    onChange: (next: DemandPolicy) => void;
};

const SOURCE_OPTIONS: Array<{ value: DemandSource; label: string }> = [
    { value: 'DRR', label: 'Run rate' },
    { value: 'FORECAST', label: 'Forecast' },
    { value: 'MAX_DRR_FORECAST', label: 'Higher of run rate and forecast' },
    { value: 'OPEN_PO', label: 'Open POs' },
    { value: 'MAX_OPEN_PO_DRR', label: 'Open POs, else run rate' },
    { value: 'COMMITTED', label: 'Committed shipments' },
    { value: 'DRR_PLUS_COMMITTED', label: 'Run rate + committed shipments' },
    { value: 'EXCLUDE', label: 'Exclude' },
];
const B2B_ONLY = new Set<DemandSource>(['COMMITTED', 'DRR_PLUS_COMMITTED']);
const COMMON_OPTIONS = SOURCE_OPTIONS.filter((o) => !B2B_ONLY.has(o.value));
const GROWN = new Set<DemandSource>(['DRR', 'FORECAST', 'MAX_DRR_FORECAST', 'MAX_OPEN_PO_DRR', 'DRR_PLUS_COMMITTED']);
const FIRM = new Set<DemandSource>(['OPEN_PO', 'MAX_OPEN_PO_DRR', 'COMMITTED', 'DRR_PLUS_COMMITTED']);

const SCOPE_LABEL: Record<GrowthScope, string> = { SKU: 'SKU', SUB_CATEGORY: 'Sub-category', CATEGORY: 'Category', GROUP: 'Group', TIER: 'Tier' };

const EMPTY: DemandPolicy = { drrWindows: [7, 14, 30], drrCombine: 'AVG', defaultSource: 'DRR', channels: [] };

const n = (v: number, d = 1): string => formatCount(v, { maxDecimals: d });
const pct = (v: number): string => `${v > 0 ? '+' : ''}${formatCount(v, { maxDecimals: 1 })}%`;

/**
 * The demand workbook: how a channel's demand per day is built, in the open. Pick the
 * run-rate windows and how they blend; grow it per channel and per product group; choose
 * what each channel reads. Each row then reads left to right as the sum it is:
 * run rate × channel growth × SKU growth (+ firm POs / committed shipments) = demand per day.
 */
const DemandWorkbook: FC<Props> = ({ demand, preview, previewing, onChange }) => {
    const policy = demand ?? EMPTY;
    const windows = useMemo(() => (policy.drrWindows.length ? policy.drrWindows : [30]), [policy.drrWindows]);
    const weights = useMemo(() => windows.map((_, i) => policy.drrWeights?.[i] ?? 1), [windows, policy.drrWeights]);
    const channelGrowth = new Map((policy.growth?.channels ?? []).map((g) => [g.channelKey, g.pct]));
    const productRules = policy.growth?.products ?? [];

    const set = (patch: Partial<DemandPolicy>): void => onChange({ ...policy, ...patch });
    const setGrowth = (patch: Partial<NonNullable<DemandPolicy['growth']>>): void => set({ growth: { ...(policy.growth ?? {}), ...patch } });
    const setChannelGrowth = (channelKey: string, value: number): void =>
        setGrowth({ channels: [...(policy.growth?.channels ?? []).filter((g) => g.channelKey !== channelKey), ...(value ? [{ channelKey, pct: value }] : [])] });
    const setChannelRule = (channelKey: string, patch: { source?: DemandSource; priority?: number | null }): void => {
        const rest = policy.channels.filter((c) => c.channelKey !== channelKey);
        const cur = policy.channels.find((c) => c.channelKey === channelKey) ?? { channelKey, source: preview?.channels.find((c) => c.channelKey === channelKey)?.source ?? policy.defaultSource };
        set({ channels: [...rest, { ...cur, ...(patch.source ? { source: patch.source } : {}), ...(patch.priority !== undefined ? { priority: patch.priority ?? undefined } : {}) }] });
    };

    const blendText = useMemo(() => {
        const parts = windows.map((w) => `${w}d`);
        if (policy.drrCombine === 'MAX') return `highest of ${parts.join(', ')}`;
        if (policy.drrCombine === 'MIN') return `lowest of ${parts.join(', ')}`;
        if (policy.drrCombine === 'WEIGHTED') {
            const total = weights.reduce((s, w) => s + w, 0) || 1;
            return `(${windows.map((w, i) => `${weights[i]}×${w}d`).join(' + ')}) ÷ ${total}`;
        }
        return `average of ${parts.join(', ')}`;
    }, [windows, weights, policy.drrCombine]);

    const columns = useMemo<DataSheetColumn<DemandPreviewChannel>[]>(
        () => [
            {
                key: 'channel',
                header: 'Channel',
                width: 170,
                sticky: true,
                render: (_v, c): ReactElement => (
                    <Stack gap={0}>
                        <Text size="sm" fw={600}>
                            {c.channelKey}
                        </Text>
                        <Text size="xs" c="dimmed">
                            {c.isB2b ? 'B2B' : (c.fulfillmentMode ?? '')}
                        </Text>
                    </Stack>
                ),
            },
            ...windows.map(
                (w): DataSheetColumn<DemandPreviewChannel> => ({
                    key: `w${w}`,
                    header: `${w}d`,
                    group: 'Run rate by window',
                    width: 76,
                    align: 'right',
                    render: (_v, c): ReactElement => (
                        <Text size="sm" c="dimmed" className={classes.num}>
                            {n(c.drrByWindow[String(w)] ?? 0)}
                        </Text>
                    ),
                }),
            ),
            {
                key: 'runRate',
                header: 'Blended',
                group: 'Run rate by window',
                width: 90,
                align: 'right',
                render: (_v, c): ReactElement => (
                    <Text size="sm" fw={600} className={classes.num}>
                        {n(c.runRatePerDay)}
                    </Text>
                ),
            },
            {
                key: 'growth',
                header: 'Channel growth',
                group: 'Growth',
                width: 120,
                render: (_v, c): ReactElement => (
                    <NumberInput size="xs" value={channelGrowth.get(c.channelKey) ?? ''} placeholder="0" suffix="%" min={-100} max={1000} onChange={(v) => setChannelGrowth(c.channelKey, Number(v) || 0)} />
                ),
            },
            {
                key: 'skuGrowth',
                header: '× SKU growth',
                group: 'Growth',
                width: 100,
                align: 'right',
                render: (_v, c): ReactElement => (
                    <Tooltip label="From the product rules below, averaged over this channel's SKUs by their run rate" withArrow>
                        <Text size="sm" c={c.productGrowthPct ? undefined : 'dimmed'} className={classes.num}>
                            {c.productGrowthPct ? pct(c.productGrowthPct) : '—'}
                        </Text>
                    </Tooltip>
                ),
            },
            {
                key: 'source',
                header: 'Use',
                width: 230,
                render: (_v, c): ReactElement => <Select size="xs" value={c.source} data={c.isB2b || B2B_ONLY.has(c.source) ? SOURCE_OPTIONS : COMMON_OPTIONS} onChange={(v) => v && setChannelRule(c.channelKey, { source: v as DemandSource })} />,
            },
            {
                key: 'grown',
                header: 'Grown rate',
                group: 'Demand / day',
                width: 96,
                align: 'right',
                render: (_v, c): ReactElement => (
                    <Text size="sm" c={GROWN.has(c.source) ? undefined : 'dimmed'} className={classes.num}>
                        {GROWN.has(c.source) ? n(c.grownPerDay) : '—'}
                    </Text>
                ),
            },
            {
                key: 'firm',
                header: '+ Firm',
                group: 'Demand / day',
                width: 96,
                align: 'right',
                render: (_v, c): ReactElement =>
                    FIRM.has(c.source) ? (
                        <Tooltip
                            withArrow
                            multiline
                            w={260}
                            label={`${n(c.source === 'OPEN_PO' || c.source === 'MAX_OPEN_PO_DRR' ? c.openPoUnits : c.committedUnits, 0)} units over the next ${preview?.horizonDays ?? 30} days, shown here as a daily average. The plan puts each shipment on its own day.`}
                        >
                            <Text size="sm" className={classes.num}>
                                {n(c.firmPerDay)}
                            </Text>
                        </Tooltip>
                    ) : (
                        <Text size="sm" c="dimmed">
                            —
                        </Text>
                    ),
            },
            {
                key: 'final',
                header: '= Final',
                group: 'Demand / day',
                width: 96,
                align: 'right',
                render: (_v, c): ReactElement => (
                    <Text size="sm" fw={700} className={classes.num}>
                        {n(c.demandPerDay)}
                    </Text>
                ),
            },
            {
                key: 'priority',
                header: 'Priority',
                width: 84,
                render: (_v, c): ReactElement => <NumberInput size="xs" min={1} value={c.priority ?? ''} placeholder="—" onChange={(v) => setChannelRule(c.channelKey, { priority: v === '' ? null : Number(v) })} />,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [policy, preview],
    );

    const groupByKey = new Map((preview?.groups ?? []).map((g) => [`${g.scope}|${g.key}`, g]));
    const ruledKeys = new Set(productRules.map((r) => `${r.scope}|${r.key}`));
    const addOptions = (['CATEGORY', 'SUB_CATEGORY', 'GROUP', 'TIER'] as GrowthScope[])
        .map((scope) => ({
            group: SCOPE_LABEL[scope],
            items: (preview?.groups ?? []).filter((g) => g.scope === scope && !ruledKeys.has(`${g.scope}|${g.key}`)).map((g) => ({ value: `${g.scope}|${g.key}`, label: `${g.key} · ${g.skus} SKUs` })),
        }))
        .filter((g) => g.items.length > 0);
    const setRule = (index: number, value: number): void => setGrowth({ products: productRules.map((r, i) => (i === index ? { ...r, pct: value } : r)) });
    const removeRule = (index: number): void => setGrowth({ products: productRules.filter((_, i) => i !== index) });
    const addRule = (value: string | null): void => {
        if (!value) return;
        const [scope, ...rest] = value.split('|');
        setGrowth({ products: [...productRules, { scope: scope as GrowthScope, key: rest.join('|'), pct: 10 }] });
    };

    const total = preview?.totalDemandPerDay ?? 0;
    const grownTotal = (preview?.channels ?? []).reduce((s, c) => s + c.grownPerDay, 0);
    const firmTotal = (preview?.channels ?? []).reduce((s, c) => s + c.firmPerDay, 0);
    const runTotal = (preview?.channels ?? []).filter((c) => GROWN.has(c.source)).reduce((s, c) => s + c.runRatePerDay, 0);

    return (
        <Stack gap="xl" mt="md">
            <div className={classes.section}>
                <div className={classes.sectionHead}>
                    <Title order={5}>1 · Run rate</Title>
                    {previewing && <Loader size="xs" />}
                </div>
                <Text size="xs" c="dimmed">
                    Past sales per day, read over the windows you pick and blended into one number per SKU and channel.
                </Text>
                <Group gap="md" align="flex-end" wrap="wrap">
                    <TagsInput
                        size="xs"
                        label="Windows (days)"
                        w={240}
                        value={windows.map(String)}
                        onChange={(v) => {
                            const next = v.map(Number).filter((x) => x > 0);
                            set({ drrWindows: next.length ? next : [30], drrWeights: undefined });
                        }}
                    />
                    <Stack gap={4}>
                        <Text size="xs" fw={500}>
                            Blend
                        </Text>
                        <SegmentedControl
                            size="xs"
                            value={policy.drrCombine}
                            onChange={(v) => set({ drrCombine: v as DemandPolicy['drrCombine'], ...(v === 'WEIGHTED' ? { drrWeights: weights } : { drrWeights: undefined }) })}
                            data={[
                                { value: 'AVG', label: 'Average' },
                                { value: 'WEIGHTED', label: 'Weighted' },
                                { value: 'MAX', label: 'Highest' },
                                { value: 'MIN', label: 'Lowest' },
                            ]}
                        />
                    </Stack>
                    {policy.drrCombine === 'WEIGHTED' &&
                        windows.map((w, i) => (
                            <NumberInput
                                key={w}
                                size="xs"
                                w={90}
                                label={`Weight ${w}d`}
                                min={0}
                                value={weights[i]}
                                onChange={(v) => set({ drrWeights: weights.map((x, j) => (j === i ? Math.max(0, Number(v) || 0) : x)) })}
                            />
                        ))}
                </Group>
                <Text size="sm" className={classes.formula}>
                    Run rate = {blendText}
                </Text>
            </div>

            <div className={classes.section}>
                <div className={classes.sectionHead}>
                    <Title order={5}>2 · Channels</Title>
                    <Select
                        size="xs"
                        w={240}
                        label="Channels without a rule read"
                        value={policy.defaultSource}
                        data={COMMON_OPTIONS}
                        onChange={(v) => v && set({ defaultSource: v as DemandSource })}
                    />
                </div>
                <Text size="xs" c="dimmed">
                    Each row reads left to right: blended run rate × channel growth × SKU growth, plus the firm part the channel reads (open POs, committed shipments), equals its demand per day. Firm numbers are never grown.
                </Text>
                {preview && <DataSheet<DemandPreviewChannel> data={preview.channels} columns={columns} getRowKey={(c) => c.channelKey} rowHeight={44} hideHeaderBar maxHeight={380} />}
            </div>

            <div className={classes.section}>
                <div className={classes.sectionHead}>
                    <Title order={5}>3 · Growth by product</Title>
                    <Select size="xs" w={280} placeholder="Add a category, group or tier" searchable data={addOptions} value={null} onChange={addRule} />
                </div>
                <Text size="xs" c="dimmed">
                    A SKU takes the most specific rate it matches: sub-category, then category, then group, then tier. It multiplies with the channel's rate: Blinkit +20% and a category +10% is ×1.32.
                </Text>
                {productRules.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No product growth set.
                    </Text>
                ) : (
                    <div>
                        {productRules.map((r, i) => {
                            const g = groupByKey.get(`${r.scope}|${r.key}`);
                            return (
                                <div key={`${r.scope}|${r.key}`} className={classes.ruleRow}>
                                    <Badge variant="light" color="gray" size="sm">
                                        {SCOPE_LABEL[r.scope]}
                                    </Badge>
                                    <Text size="sm" fw={600} truncate>
                                        {r.key}
                                    </Text>
                                    <NumberInput size="xs" value={r.pct} suffix="%" min={-100} max={1000} onChange={(v) => setRule(i, Number(v) || 0)} />
                                    <Text size="xs" c="dimmed" className={classes.num}>
                                        {g ? `${g.skus} SKUs · ${n(g.runRatePerDay)}/day → ${pct(r.pct)} adds ${n((g.runRatePerDay * r.pct) / 100)}/day before channel growth` : 'not in the catalogue now'}
                                    </Text>
                                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => removeRule(i)} aria-label="Remove">
                                        <Trash size={14} />
                                    </ActionIcon>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {preview && (
                <div className={classes.section}>
                    <Title order={5}>4 · The final number</Title>
                    <Text size="sm" className={classes.formula}>
                        Run rate {n(runTotal)} → grown {n(grownTotal)} ({runTotal > 0 ? pct((grownTotal / runTotal - 1) * 100) : '—'}) + firm {n(firmTotal)} = <b>{n(total)} units / day</b> across channels, over the next {preview.horizonDays} days
                    </Text>
                    <Text size="xs" c="dimmed">
                        Firm demand is shown here as a daily average so the channels add up. In the plan each committed shipment and open PO sits on its own due date, so a single large shipment makes one sharp step in the stock curve, not a gentle slope. A channel on "Run rate + committed shipments" counts both: its run rate already includes past shipments to that partner, so use it only when the committed lines are extra to the usual flow. Each SKU's own breakdown, with the shipments it counts, is in the explanation panel on the plan.
                    </Text>
                </div>
            )}
        </Stack>
    );
};

export default DemandWorkbook;
